import { Cpu, Users, Globe, Heart } from 'lucide-react';

export default function About() {
  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', padding: '0 1.5rem 3rem' }}>
      <div style={{ maxWidth: '780px', margin: '0 auto' }}>
        <div style={{ padding: '2.5rem 0 2rem', textAlign: 'center' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.5rem' }}>
            About <span style={{ color: 'var(--color-primary)' }}>DIYElectronicMech</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', maxWidth: '520px', margin: '0 auto', fontSize: '0.9rem' }}>
            Built by makers, for makers — an AI-powered electronics repair and learning platform.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {[
            {
              icon: Heart,
              color: 'green',
              title: 'Our Mission',
              content: `DIYElectronicMech exists to democratize electronics knowledge. Whether you're a weekend hobbyist, a seasoned repair technician, or a first-time maker, we believe everyone deserves access to powerful diagnostic tools and clear, practical guidance.

Too often, broken electronics end up in landfills simply because the owner didn't know how to fix them. We're changing that.`,
            },
            {
              icon: Cpu,
              color: 'cyan',
              title: 'What We Do',
              content: `We combine AI-powered electronics diagnostics, a comprehensive component lookup engine, circuit schematic search, and curated maker projects — all in one platform.

Our AI assistant, ElectrAI, is trained on electronics repair guides, datasheets, Arduino documentation, and maker community knowledge. It provides step-by-step troubleshooting guidance, component identification, and project planning.`,
            },
            {
              icon: Users,
              color: 'purple',
              title: 'Who It\'s For',
              content: `• Makers and hobbyists building Arduino, Raspberry Pi, and ESP32 projects
• Electronics repair technicians fixing consumer electronics
• Students learning electronics fundamentals
• Engineers needing quick component and datasheet lookups
• Anyone who's ever said "I wonder how this circuit works"`,
            },
            {
              icon: Globe,
              color: 'amber',
              title: 'The Platform',
              content: `DIYElectronicMech is part of the DIY-Mech family of platforms — specialized AI tools built for different types of repair and making. Each site shares a powerful AI backend but is tailored to its specific domain.

We're constantly improving ElectrAI with new knowledge sources, better component databases, and community-contributed project guides.`,
            },
          ].map(({ icon: Icon, color, title, content }) => {
            const colors: Record<string, { text: string; bg: string; border: string }> = {
              green: { text: 'var(--color-primary)', bg: 'rgba(0,255,136,0.08)', border: 'rgba(0,255,136,0.2)' },
              cyan: { text: 'var(--color-secondary)', bg: 'rgba(0,207,255,0.08)', border: 'rgba(0,207,255,0.2)' },
              amber: { text: 'var(--color-accent)', bg: 'rgba(255,170,0,0.08)', border: 'rgba(255,170,0,0.2)' },
              purple: { text: 'var(--color-purple)', bg: 'rgba(168,85,247,0.08)', border: 'rgba(168,85,247,0.2)' },
            };
            const c = colors[color];
            return (
              <div key={title} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '14px', padding: '1.75rem', transition: 'border-color 0.2s ease' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = c.border}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
                  <div style={{ width: '38px', height: '38px', background: c.bg, border: `1px solid ${c.border}`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={19} color={c.text} />
                  </div>
                  <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>{title}</h2>
                </div>
                <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.8, whiteSpace: 'pre-line' }}>{content}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
